import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { environment } from '@environment/environment';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { IUser } from '@shared/interfaces/user.interface';
import { LoggerService } from '@shared/services/logger.service';
import { SidebarService } from '@shared/services/sidebar.service';
import { DataRetentionService } from '@shared/services/data-retention.service';
import { LocalStorage, SessionStorage } from 'ngx-webstorage';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConfigurationNavbarComponent implements OnInit, OnDestroy {
  @SessionStorage() public user: IUser;
  @SessionStorage() public hiringFirm: IHiringFirm;
  @SessionStorage() public settings: ISetting;

  @LocalStorage() public canManageSubAccount: boolean;

  @LocalStorage() private isSubAccount: boolean;

  public links: {
    id: number;
    label: string;
    items: {
      label: string;
      icon: string;
      url: string;
      step?: number;
      badge?: number;
    }[];
  }[] = [
    {
      id: 0,
      label: 'NAVBAR.CONFIGURATION',
      items: []
    }
  ];

  public rootLinks: { label: string; icon: string; url: string }[] = [];
  public isProduction: boolean = environment.IS_PRODUCTION;
  public params: any = {
    environment: environment.ENVIRONMENT
  };

  public scheduledEntitiesCount: number = 0;

  private readonly constructorName: string = String(this.constructor.name);
  private destroy$ = new Subject<void>();
  private reloadDataSetSubscription: Subscription;

  constructor(
    private readonly _logger: LoggerService,
    private readonly _sidebar: SidebarService,
    private readonly _dataRetention: DataRetentionService
  ) {
    const url = 'Reload sub account dataset';
    this.reloadDataSetSubscription =
      this._sidebar.reloadDatasetCalled$.subscribe(
        (res: any) => {
          this._logger.info(this.constructorName, url, res);

          // REFRESH LINKS HERE
          this.setLinks();
        },
        (err: any) => this._logger.error(this.constructorName, url, err)
      );
  }

  ngOnInit(): void {
    this.setLinks();
    this.loadScheduledEntitiesCount();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reloadDataSetSubscription?.unsubscribe();
  }

  public trackByFn(i: any) {
    return i.id;
  }

  private setLinks() {
    this.settings = this.hiringFirm?.settings;

    this.links[0].items = [
      {
        label: 'NAVBAR.SETTINGS',
        url: '/settings/general',
        icon: 'lnr-cog',
        step: 1
      },
      {
        label: 'NAVBAR.PLUGINS',
        url: '/plugins',
        icon: 'lnr-equalizer',
        step: 3
      },
      {
        label: 'NAVBAR.INTEGRATIONS',
        url: '/integrations',
        icon: 'lnr-network',
        step: 4
      },
      {
        label: 'NAVBAR.USERS',
        url: '/users',
        icon: 'lnr-users2',
        step: 5
      }
    ];

    if (!this.settings?.is_billing_sub_accounts_linked_enabled) {
      this.links[0].items.splice(1, 0, {
        label: 'NAVBAR.USAGE',
        url: '/usage',
        icon: 'lnr-chart-bars',
        step: 2
      });
    }

    // The history names who changed what: root recruiters only, same gate as
    // the endpoint. Sits right after Integrations.
    if (this.user?.isRootRecruiter) {
      const integrationsIndex = this.links[0].items.findIndex(
        (l: any) => l.label === 'NAVBAR.INTEGRATIONS'
      );
      this.links[0].items.splice(integrationsIndex + 1, 0, {
        label: 'NAVBAR.CHANGE_HISTORY',
        url: '/change-history',
        icon: 'lnr-history'
      });
    }

    if (this.settings?.is_demo_enabled) {
      // /settings/jobs: the first tab that isn't demo-disabled.
      this.links[0].items[0].url = '/settings/jobs';
    } else {
      this.links[0].items[0].url = '/settings';
    }

    const isAccountsEnabled = this.links[0].items.find(
      (l: any) => l.label === 'NAVBAR.ACCOUNTS'
    );

    if (
      !isAccountsEnabled &&
      !this.isSubAccount &&
      this.settings?.is_sub_hiring_firm_enabled
    ) {
      this.setAccountsLink();
    } else {
      this.links[0].items = this.links[0].items.filter(
        (l: any) => l.label !== 'NAVBAR.ACCOUNTS'
      );
    }
  }

  private setAccountsLink() {
    this.links[0].items.push({
      label: 'NAVBAR.ACCOUNTS',
      url: '/accounts',
      icon: 'lnr-site-map'
    });
  }

  private loadScheduledEntitiesCount(): void {
    // Endpoint is root-recruiter only and gated behind the admin-granted
    // data_retention plugin — skip otherwise to avoid 403s
    if (
      !this.settings?.plugins_json?.data_retention_plugin ||
      !this.settings?.settings_json?.data_retention?.is_enabled ||
      !this.user?.isRootRecruiter
    ) {
      return;
    }

    this._dataRetention
      .getScheduledEntities(30)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.scheduledEntitiesCount = result.candidates.length;
          this.updateScheduledEntitiesLink();
        },
        error: (err) => {
          this._logger.error(
            this.constructorName,
            'Error loading scheduled entities',
            err
          );
        }
      });
  }

  private updateScheduledEntitiesLink(): void {
    if (this.scheduledEntitiesCount === 0) {
      return;
    }

    // Check if link already exists
    const existingLink = this.links[0].items.find(
      (l: any) => l.url === '/scheduled-entities'
    );

    if (existingLink) {
      existingLink.badge = this.scheduledEntitiesCount;
    } else {
      // Add the scheduled entities link after settings
      this.links[0].items.splice(1, 0, {
        label: 'NAVBAR.SCHEDULED_ENTITIES',
        url: '/scheduled-entities',
        icon: 'lnr-calendar-full',
        badge: this.scheduledEntitiesCount
      });
    }
  }
}
